import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import type { AnalyticsEvent, AnalyticsEventProps } from './analytics-events';

import {
  getBooleanEnvVar,
  getEnvVar,
  getEnvVarWithDefault,
  isDevelopment,
} from './env';

let sentryEnabled = false;

/** Observability must never break the app: failures are only logged. */
function warnFailure(what: string, err: unknown): void {
  // eslint-disable-next-line no-console -- the one intended console use here
  console.warn(what, err);
}

let posthogEnabled = false;

export function initObservability(): void {
  initSentry();
  initPostHog();
}

function initSentry(): void {
  const dsn = getEnvVar('VITE_SENTRY_DSN');
  const flagOn = getBooleanEnvVar('VITE_ENABLE_ERROR_REPORTING');

  if (!flagOn || !dsn || dsn === 'your_sentry_dsn') {
    return;
  }

  Sentry.init({
    dsn,
    environment: getEnvVarWithDefault(
      'VITE_APP_ENVIRONMENT',
      import.meta.env.MODE
    ),
    release: getEnvVar('VITE_APP_VERSION') || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    tracesSampleRate: isDevelopment() ? 1.0 : 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    enableLogs: true,
  });

  sentryEnabled = true;
}

function initPostHog(): void {
  const key = getEnvVar('VITE_POSTHOG_KEY');
  const flagOn = getBooleanEnvVar('VITE_ENABLE_ANALYTICS');

  if (!flagOn || !key || key === 'your_posthog_key') {
    return;
  }

  // Same-origin path proxied to PostHog (vercel.json rewrites, the Vite dev
  // proxy locally), so ad blockers that block *.posthog.com don't drop events.
  const host = getEnvVarWithDefault('VITE_POSTHOG_HOST', '/ingest');
  const environment = getEnvVarWithDefault(
    'VITE_APP_ENVIRONMENT',
    import.meta.env.MODE
  );

  posthog.init(key, {
    api_host: host,
    ui_host: 'https://us.posthog.com',
    // The app is an SPA: `true` would only record the first load.
    capture_pageview: 'history_change',
    // Off in the app: in an SPA it nearly doubles volume for little insight,
    // and the free plan is 1M events/month. The landing page keeps it (scroll
    // depth, time on page).
    capture_pageleave: false,
    autocapture: false,
    // Replay is sampled in PostHog project settings (10% of sessions, see
    // scripts/posthog-setup.mjs). Learners include children, so nothing a
    // learner reads or types is recorded: every text node and input is masked
    // and images/video are blocked. Network bodies/headers are never captured.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: 'img, video',
      recordCrossOriginIframes: false,
      recordHeaders: false,
      recordBody: false,
    },
    person_profiles: 'identified_only',
    // The anonymous id lives on .xonera.xyz, so a visitor from the landing
    // page is the same person once they sign up here.
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    loaded: ph => {
      ph.register({ environment, site: 'app' });
    },
  });

  posthogEnabled = true;
}

export function identifyUser(
  userId: string,
  email?: string,
  traits?: Record<string, unknown>
): void {
  const props: Record<string, unknown> = { ...(traits ?? {}) };
  if (email) props.email = email;

  if (posthogEnabled) {
    try {
      posthog.identify(userId, props);
    } catch (err) {
      warnFailure('PostHog identify failed', err);
    }
  }

  if (sentryEnabled) {
    try {
      Sentry.setUser({ id: userId, ...(email ? { email } : {}) });
    } catch (err) {
      warnFailure('Sentry setUser failed', err);
    }
  }
}

/**
 * Record a product event. A no-op unless PostHog was initialised, so tests,
 * Storybook and CI never send anything. Props are ids, enums and counts only -
 * never free text a learner typed (see analytics-events.ts).
 */
export function track<E extends AnalyticsEvent>(
  event: E,
  ...props: AnalyticsEventProps<E> extends Record<string, never>
    ? []
    : [AnalyticsEventProps<E>]
): void {
  if (!posthogEnabled) return;
  try {
    posthog.capture(event, props[0]);
  } catch (err) {
    warnFailure('PostHog capture failed', err);
  }
}

/** Person properties used to segment every insight (role, tier, ...). */
export function setUserTraits(traits: Record<string, unknown>): void {
  if (!posthogEnabled) return;
  try {
    posthog.setPersonProperties(traits);
  } catch (err) {
    warnFailure('PostHog setPersonProperties failed', err);
  }
}

export function resetIdentity(): void {
  if (posthogEnabled) {
    try {
      posthog.reset();
    } catch (err) {
      warnFailure('PostHog reset failed', err);
    }
  }
  if (sentryEnabled) {
    try {
      Sentry.setUser(null);
    } catch (err) {
      warnFailure('Sentry setUser(null) failed', err);
    }
  }
}

export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (!sentryEnabled) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export function isPosthogEnabled(): boolean {
  return posthogEnabled;
}
